import { useState } from 'react';
import { Container, Box, Card, Grid, Flex } from 'components/box';
import MenuLinks from 'components/menu-links';
import {
  PageTitle,
  SectionHeading,
  SmallHeader,
  Text,
  ParagraphText,
} from 'components/typography';
import { css, Theme } from '@emotion/react';
import styled from 'components/styled';
import RichText from 'components/rich-text';
import { Link } from 'components/configurable-routing';
import Button from 'components/button';
import dynamic from 'next/dynamic';
import Step1Icon from 'shared/assets/icons/page.svg';
import Step2Icon from 'shared/assets/icons/pages-with-tick.svg';
import Step3Icon from 'shared/assets/icons/promotion-sign.svg';
import Step4Icon from 'shared/assets/icons/signoff.svg';
import Step5Icon from 'shared/assets/icons/clock-sign.svg';
import Step6Icon from 'shared/assets/icons/trolley-dash.svg';
import Step7Icon from 'shared/assets/icons/package-sell-with-us.svg';
import Step8Icon from 'shared/assets/icons/payment.svg';
import Step9Icon from 'shared/assets/icons/delivery.svg';
import formatNumber from '../util/format-number';
import PlayIcon from 'shared/resources/images/sell-with-us/play.svg';
import resourceUrl from '../util/make-url';
import { PageProps } from 'types/types';
import { GetStaticPropsResult } from 'next';
import config from '../util/load-config';
import { prepMetaTags } from '../util/seo';
import AutoplayCarousel from 'components/_shared/widgets/autoplay-carousel';
import Quotes from 'shared/assets/icons/quotes.svg';
import { canonicalUrl } from '../util/url';

const Modal = dynamic(
  () => import('components/_shared/widgets/modal/with-close')
);

const TestimonialItem = ({
  content,
  supplierImage,
  alt,
}: {
  content: string;
  supplierImage: string;
  alt: string;
}) => (
  <Grid mx="45px" width="100%" gridTemplateRows="auto 1fr" height="100%">
    <Quotes
      css={css`
        margin: auto auto 0 auto;
      `}
    />
    <Flex width="100%" justifyContent="center" flexDirection="column">
      <ParagraphText textAlign="center">{content}</ParagraphText>
      <img
        src={supplierImage}
        alt={alt}
        css={css`
          margin: 18px auto auto auto;
          max-width: 100%;
        `}
      />
    </Flex>
  </Grid>
);

const carouselSlides = [
  {
    id: 'core-group',
    element: (
      <TestimonialItem
        content={
          `Our favourite thing about working with OneDayOnly` +
          ` is the speed at which you can turn deals on along with how` +
          ` amazing you make the deals look! Beautifully curated.`
        }
        alt="Core Group"
        supplierImage={resourceUrl('odo/testimonials/core-group-logo.webp', {
          height: 30,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'creative-housewares',
    element: (
      <TestimonialItem
        content={
          `It has been highly beneficial to our business to` +
          ` trade on eCommerce sites such as OneDayOnly. The modern age` +
          ` has moved towards online and digital platforms. With the` +
          ` assistance of OneDayOnly, Creative Housewares has managed` +
          ` to attract the attention of the modern age buyer. We at` +
          ` Creative Housewares have seen our market share for online` +
          ` purchases increase with the assistance of OneDayOnly.`
        }
        alt="Creative Housewares"
        supplierImage={resourceUrl(
          'odo/testimonials/creative-houseware-logo.jpeg',
          {
            height: 25,
            isAssetUrl: true,
          }
        )}
      />
    ),
  },
  {
    id: 'cielo',
    element: (
      <TestimonialItem
        content={
          `Our favourite thing about working with` +
          ` OneDayOnly is the amazing staff who make our work such a pleasure!`
        }
        alt="Cielo"
        supplierImage={resourceUrl('odo/testimonials/cielo-logo.png', {
          height: 80,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'brooks-sales',
    element: (
      <TestimonialItem
        content="Payment is made very soon after delivery, on presentation of an invoice!"
        alt="Brooks Sales"
        supplierImage={resourceUrl('odo/testimonials/brooks-sales-logo.jpg', {
          height: 80,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'albatros-travel',
    element: (
      <TestimonialItem
        content={
          `The process of working with OneDayOnly is hassle-free.` +
          ` No exorbitant fees/commissions and the platform has opened up` +
          ` a whole new market for us, domestic travelers. We have promoted` +
          ` new travel deals and some of these specials are exclusive to OneDayOnly.`
        }
        alt="Albatros Travel"
        supplierImage={resourceUrl('odo/testimonials/albatros-logo.jpeg', {
          height: 100,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'calasca',
    element: (
      <TestimonialItem
        content={
          `We have been working with OneDayOnly for 11` +
          ` years and they treat us like literal family. We can't` +
          ` believe the capabilities of their graphics team and` +
          ` the dedication and support the full team offers on a` +
          ` daily basis. They go above and beyond to help small,` +
          ` medium, and large-sized businesses to grow their brands` +
          ` and offerings. They have made our brand stay known and` +
          ` relevant to households.`
        }
        alt="Calasca"
        supplierImage={resourceUrl('odo/testimonials/calasca-logo.png', {
          height: 60,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'diageo',
    element: (
      <TestimonialItem
        content={
          `We've found the process of working with OneDayOnly` +
          ` very smooth and the automate administrative processes are very efficient.`
        }
        alt="Diageo"
        supplierImage={resourceUrl('odo/testimonials/diageo-logo.jpeg', {
          height: 50,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'everlast',
    element: (
      <TestimonialItem
        content={
          `Demand for our products have increased since working` +
          ` with OneDayOnly. Without a doubt! It has given our brand huge` +
          ` exposure and allowed us to tap into a whole new customer market.`
        }
        alt="Everlast"
        supplierImage={resourceUrl('odo/testimonials/everlast-logo.jpg', {
          height: 70,
          isAssetUrl: true,
        })}
      />
    ),
  },
  {
    id: 'phoenix',
    element: (
      <TestimonialItem
        content={
          `One of the reasons we use OneDayOnly is to accelerate sales,` +
          ` leveraging their great platform capabilities which enables the best` +
          ` price offering, that results in extremely successful sales. Their` +
          ` process is seamless and smooth and our favourite part about working` +
          ` with them for 10 years is definitely their team.`
        }
        alt="Phoenix"
        supplierImage={resourceUrl(
          'odo/testimonials/phoenix-fmcg-group-logo.png',
          {
            height: 80,
            isAssetUrl: true,
          }
        )}
      />
    ),
  },
  {
    id: 'smd-technologies',
    element: (
      <TestimonialItem
        content={
          `Our products and our brands have absolutely gained momentum` +
          ` in the market since working with OneDayOnly. Not only do we generate` +
          ` more sales, but it is an amazing platform for generating brand awareness` +
          ` and for launching new brands and their associated products.`
        }
        alt="SMD Technologies"
        supplierImage={resourceUrl(
          'odo/testimonials/smd-technologies-logo.jpeg',
          {
            height: 90,
            isAssetUrl: true,
          }
        )}
      />
    ),
  },
];

const supplierLogos = [
  {
    name: 'Unilever',
    logoUrl: resourceUrl('odo/sell-with-us/logos/unilever.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'JBL',
    logoUrl: resourceUrl('odo/sell-with-us/logos/jbl.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Dell',
    logoUrl: resourceUrl('odo/sell-with-us/logos/dell.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Reebok',
    logoUrl: resourceUrl('odo/sell-with-us/logos/reebok.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },

  {
    name: 'Barbie',
    logoUrl: resourceUrl('odo/sell-with-us/logos/barbie.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Fisher Price',
    logoUrl: resourceUrl('odo/sell-with-us/logos/fisher-price.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Epson',
    logoUrl: resourceUrl('odo/sell-with-us/logos/epson.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Wilson',
    logoUrl: resourceUrl('odo/sell-with-us/logos/wilson.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Distell',
    logoUrl: resourceUrl('odo/sell-with-us/logos/distell.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Columbia',
    logoUrl: resourceUrl('odo/sell-with-us/logos/columbia.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Colgate-Palmolive',
    logoUrl: resourceUrl('odo/sell-with-us/logos/colgate.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Dettol',
    logoUrl: resourceUrl('odo/sell-with-us/logos/dettol.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Everlast',
    logoUrl: resourceUrl('odo/sell-with-us/logos/everlast.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Finish',
    logoUrl: resourceUrl('odo/sell-with-us/logos/finish.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'FitFlop',
    logoUrl: resourceUrl('odo/sell-with-us/logos/fitflop.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Harpic',
    logoUrl: resourceUrl('odo/sell-with-us/logos/harpic.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Hunter',
    logoUrl: resourceUrl('odo/sell-with-us/logos/hunter.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Lego',
    logoUrl: resourceUrl('odo/sell-with-us/logos/lego.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Mortein',
    logoUrl: resourceUrl('odo/sell-with-us/logos/mortein.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Revlon',
    logoUrl: resourceUrl('odo/sell-with-us/logos/revlon.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Ugg',
    logoUrl: resourceUrl('odo/sell-with-us/logos/ugg.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Vanish',
    logoUrl: resourceUrl('odo/sell-with-us/logos/vanish.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Yamaha',
    logoUrl: resourceUrl('odo/sell-with-us/logos/yamaha.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
  {
    name: 'Megamaster',
    logoUrl: resourceUrl('odo/sell-with-us/logos/megamaster.png', {
      width: 200,
      isAssetUrl: true,
    }),
  },
];

const ourSteps = [
  {
    label: 'Application',
    text:
      "<p>Apply <a style='color: #0093d0;font-weight:bold;'href='/become-a-supplier' " +
      '>here</a></p>',
    icon: Step1Icon,
  },
  {
    label: 'Approval and onboarding',
    text: 'Review application and get the ball rolling',
    icon: Step2Icon,
  },
  {
    label: 'The deal',
    text:
      'Send us all the relevant deal information and we&apos;ll set it up for you',
    icon: Step3Icon,
  },
  {
    label: 'Sign off',
    text:
      'Electronic approval link gets sent to you for your confirmation and approval the day before the deal goes live',
    icon: Step4Icon,
  },
  {
    label: 'Sales',
    text: 'Deal goes live from midnight for 24 hours.',
    icon: Step5Icon,
  },
  {
    label: 'Order',
    text: 'We place our order the following day for units sold',
    icon: Step6Icon,
  },
  {
    label: 'Deliver to OneDayOnly',
    text:
      'Delivery of stock to one or both of our warehouses in Ndabeni, Cape Town or Steeledale' +
      ', Joburg to be made within THREE days of the order being placed',
    icon: Step7Icon,
  },
  {
    label: 'Payment',
    text: 'We pay on invoice once the stock has been delivered',
    icon: Step8Icon,
  },
  {
    label: 'OneDayOnly delivers to customer',
    text: 'Happy customers experiencing your brand',
    icon: Step9Icon,
  },
];

const flexStyle = (theme: Theme) => css`
  @media ${theme.mediaQueries.desktopUp} {
    display: flex;
  }
`;

const imgStyle = (theme: Theme) => css`
  width: 100%;
  border-radius: ${theme.space[2]}px;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SvgIcon = ({ svg, ...rest }: any) => {
  const Svg = svg;
  return <Svg {...rest} />;
};

const SvgWrapper = styled(Box)`
  ${({ theme }) => css`
    svg path,
    svg polygon {
      fill: ${theme.colors.mediumGrey} !important;
    }
  `}
`;

const Icon = ({ step }: { step: number }) => (
  <Flex
    mb="3"
    borderRadius="50%"
    backgroundColor="primary"
    width="40px"
    height="40px"
    alignItems="center"
    justifyContent="center"
    css={css`
      position: absolute;
      top: 0;
      left: -21px;
    `}
  >
    <Text
      textAlign="center"
      fontSize={2}
      fontWeight="bold"
      fontFamily="header"
      color="white"
    >
      {step}
    </Text>
  </Flex>
);

const Steps = () => {
  return (
    <Flex pl={4} justifyContent="center">
      <Box>
        {ourSteps.map((step, idx) => (
          <Flex
            flexDirection={['column', 'row']}
            key={idx}
            pb={4}
            pl={[3, 4]}
            ml={[0, 2, 4]}
            css={theme =>
              css`
                position: relative;
                border-left: 2px solid ${theme.colors.blue};
                :last-child {
                  border-left: none;
                }
              `
            }
          >
            <Icon step={idx + 1} />
            <Flex flexDirection={['column', 'row']}>
              <Box px="4">
                <SvgWrapper
                  css={css`
                    width: 50px;
                  `}
                >
                  <SvgIcon
                    preserveAspectRatio="xMidYMid meet"
                    css={css`
                      width: 100%;
                      height: auto;
                    `}
                    svg={step.icon}
                  />
                </SvgWrapper>
              </Box>
              <Box
                px="3"
                css={css`
                  max-width: 350px;
                `}
              >
                <Text mb={3} mt={[3, 0]} fontFamily="header" fontWeight="bold">
                  {step.label}
                </Text>
                <Text lineHeight="1.5" fontSize="small" color="darkGrey">
                  <RichText content={step.text} unstyled />
                </Text>
              </Box>
            </Flex>
          </Flex>
        ))}
      </Box>
    </Flex>
  );
};

const SellWithUs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Container>
        <Box css={flexStyle}>
          <MenuLinks title="Sell With Us" slug="sell-with-us" />

          <Box>
            <Box>
              <PageTitle mb="3">Sell your products with us</PageTitle>
              <ParagraphText width={['95%', null, '80%']} mb={2}>
                Welcome to OneDayOnly!{' '}
              </ParagraphText>

              <ParagraphText width={['95%', null, '80%']} mb={2}>
                Think your brand and product offering is the perfect fit for
                ours? You&apos;re in the right place! We&apos;re the go-to guys
                for kickstarting businesses, accelerating sales and getting your
                stuff right in front of a huge and ever-growing online audience.
                With over 2 million subscribers receiving our daily newsletters
                and visiting our site every day, your product could be exactly
                the thing they&apos;re looking for.
              </ParagraphText>

              <ParagraphText width={['95%', null, '80%']}>
                We pride ourselves on our vast, diverse, and high-quality range
                of products and stockists - so if that sounds like you, keep
                reading!
              </ParagraphText>

              <Card p={[3, 3, 4]} borderRadius="2" my="4">
                <SectionHeading textAlign="center">
                  Why OneDayOnly?
                </SectionHeading>

                <Grid
                  gridRowGap={[2, 3, 4]}
                  gridColumnGap={[2, 3, 4]}
                  gridTemplateColumns={['1fr', '1fr 1fr', '1fr 1fr 1fr']}
                >
                  <Box>
                    <img
                      css={imgStyle}
                      src={resourceUrl('odo/sell-with-us/img-1.jpeg', {
                        width: 300,
                        isAssetUrl: true,
                      })}
                      alt="img-1"
                    />
                    <SmallHeader mt={[1, 2]}>
                      Quick and easy onboarding process
                    </SmallHeader>
                    <ParagraphText>
                      We&apos;ve streamlined our onboarding process to make it
                      as easy as possible for you. If your product is approved,
                      it will then be set up before we send you an approval link
                      and your deal can go live in just a few days.
                    </ParagraphText>
                  </Box>

                  <Box>
                    <img
                      css={imgStyle}
                      src={resourceUrl('odo/sell-with-us/img-2.jpeg', {
                        width: 300,
                        isAssetUrl: true,
                      })}
                      alt="img-1"
                    />
                    <SmallHeader mt={[1, 2]}>Reach new audiences</SmallHeader>
                    <ParagraphText>
                      OneDayOnly opens your brand up to 1 000 000+ daily
                      newsletter subscribers and over 650 000 followers across
                      our active and engaging social media platforms
                    </ParagraphText>
                  </Box>

                  <Box>
                    <img
                      css={imgStyle}
                      src={resourceUrl('odo/sell-with-us/img-3.jpeg', {
                        width: 300,
                        isAssetUrl: true,
                      })}
                      alt="img-1"
                    />
                    <SmallHeader mt={[1, 2]}>Market your products</SmallHeader>
                    <ParagraphText>
                      Not only will your product be marketed directly to our
                      hundreds of thousands of fans, it will be featured
                      alongside some of the world&apos;s biggest brands.
                    </ParagraphText>
                  </Box>

                  <Box>
                    <img
                      css={imgStyle}
                      src={resourceUrl('odo/sell-with-us/img-4.jpeg', {
                        width: 300,
                        isAssetUrl: true,
                      })}
                      alt="img-1"
                    />
                    <SmallHeader mt={[1, 2]}>Move stock</SmallHeader>
                    <ParagraphText>
                      Being a daily deals site affords us the opportunity to
                      move large amounts of stock you might have on hand
                      relatively quickly, provided we can reach a deal that we
                      both agree our audience will jump at.
                    </ParagraphText>
                  </Box>

                  <Box mb={[3, 0]}>
                    <img
                      css={imgStyle}
                      src={resourceUrl('odo/sell-with-us/img-5.jpeg', {
                        width: 300,
                        isAssetUrl: true,
                      })}
                      alt="img-1"
                    />
                    <SmallHeader mt={[1, 2]}>
                      Quick and Easy payment
                    </SmallHeader>
                    <ParagraphText>
                      Once we know how much stock has been sold, we will pay for
                      the stock once delivered and checked at our distribution
                      centres. No waiting period!
                    </ParagraphText>
                  </Box>

                  <Flex
                    borderRadius="2"
                    backgroundColor="lightBlue"
                    px={2}
                    py={3}
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <SmallHeader fontSize={[2, 3]} my="2" textAlign="center">
                      Ready to start selling?
                    </SmallHeader>
                    <Link my="2" href="/become-a-supplier">
                      <Button mb="2" px={[3, 1, 3]} size="small">
                        Apply to sell with Us
                      </Button>
                    </Link>
                  </Flex>
                </Grid>
              </Card>
            </Box>

            {/* How we work */}
            <Card borderRadius="2" p="0" mb="4">
              <Box p={4}>
                <SectionHeading mt={3} mb={3} textAlign="center" fontSize="3">
                  How we work
                </SectionHeading>
                <Flex
                  css={css`
                    display: none;
                  `}
                  flexDirection={['column', null, 'row']}
                  mb={4}
                  mt={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <ParagraphText
                    mb={[3, 0]}
                    mr={[0, 3]}
                    textAlign={['center', 'left']}
                    lineHeight="1.2"
                  >
                    Watch this video to find out more about how we work.
                  </ParagraphText>
                  <Button
                    onClick={() => setIsModalOpen(true)}
                    variant="flat"
                    p="0px"
                    mt={[2, null, 0]}
                    size="small"
                    color="darkGrey"
                  >
                    <PlayIcon
                      css={css`
                        margin-right: 8px;
                      `}
                    />{' '}
                    Watch Video
                  </Button>
                </Flex>
                <Steps />
              </Box>

              <Flex
                p={3}
                justifyContent="space-between"
                alignItems="center"
                backgroundColor="lightBlue"
                flexDirection={['column', 'row']}
              >
                <SmallHeader textAlign="center" py={3}>
                  Ready to start selling?
                </SmallHeader>
                <Link href="/become-a-supplier">
                  <Button px={4} py={2} my={2}>
                    Apply Now
                  </Button>
                </Link>
              </Flex>
            </Card>
            <Card py={4} borderRadius="2" my="3">
              <SectionHeading textAlign="center" py={3} fontSize="3">
                Supplier Testimonials
              </SectionHeading>
              <AutoplayCarousel slides={carouselSlides} />
            </Card>

            {/* Brands we work with*/}
            <Card py={4} borderRadius="2" my="3">
              <SectionHeading textAlign="center" py={3} fontSize="3">
                Brands we work with
              </SectionHeading>
              <Flex flexWrap="wrap" justifyContent="center" ml={-4}>
                {supplierLogos.map(supplier => (
                  <Box key={supplier.name} ml={4} mb={3} width="15%">
                    <img
                      css={imgStyle}
                      src={supplier.logoUrl}
                      alt={`Logo of ${supplier.name}`}
                    />
                  </Box>
                ))}
              </Flex>
            </Card>

            {/* Followers
             * TODO: Get social media fans/followers dynamically - NEMO-798
             */}
            <Flex
              justifyContent="space-between"
              alignItems="center"
              px={4}
              py={3}
              backgroundColor="lightBlue"
              borderRadius="2"
              flexDirection={['column', null, 'row']}
            >
              <Flex alignItems="center" mb={[2, null, 0]}>
                <img
                  src={resourceUrl('odo/sell-with-us/logos/facebook.png', {
                    width: 100,
                    isAssetUrl: true,
                  })}
                  width="30px"
                  alt="Facebook"
                />
                <SmallHeader ml={3} marginBottom={0}>
                  {formatNumber(config.social.facebookFollowerCount)} fans
                </SmallHeader>
              </Flex>
              <Flex alignItems="center" mb={[2, null, 0]}>
                <img
                  src={resourceUrl('odo/sell-with-us/logos/twitter.png', {
                    width: 100,
                    isAssetUrl: true,
                  })}
                  width="30px"
                  alt="Twitter"
                />
                <SmallHeader ml={3} marginBottom={0}>
                  {formatNumber(config.social.twitterFollowerCount)} followers
                </SmallHeader>
              </Flex>
              <Flex alignItems="center">
                <img
                  src={resourceUrl('odo/sell-with-us/logos/instagram.png', {
                    width: 100,
                    isAssetUrl: true,
                  })}
                  width="30px"
                  alt="Instagram"
                />
                <SmallHeader ml={3} marginBottom={0}>
                  {formatNumber(config.social.instagramFollowerCount)} followers
                </SmallHeader>
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Container>

      <Modal
        close={() => setIsModalOpen(false)}
        size="small"
        isOpen={isModalOpen}
      >
        <Box width="100%" height="50vh">
          <iframe
            title="product-video"
            // TODO: Replace url with the correct video url - NEMO-799
            src="https://www.youtube.com/embed/mcYnMbJwFCA"
            frameBorder="0"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="origin"
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
            `}
          />
        </Box>
      </Modal>
    </>
  );
};

export async function getStaticProps(): Promise<
  GetStaticPropsResult<PageProps>
> {
  const meta = config.seo.meta.pages['sell-with-us'];

  return {
    props: {
      canonicalUrl: canonicalUrl('sell-with-us'),
      ...(meta ? { layout: { metaTags: prepMetaTags(meta) } } : {}),
    },
  };
}

export default SellWithUs;
